import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDotBotsAuth } from '@dotbots-boutique/auth-sdk/react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useApi } from '../lib/api';
import { hasPermission } from '../permissions';
import { t, tp } from '../lib/i18n';
import Leaderboard from '../components/Leaderboard';
import type { Campaign, CampaignReward, CampaignStats } from '../lib/types';

const COLORS = ['#38bdf8', '#10b981', '#f59e0b', '#ef4444', '#a855f7', '#ec4899'];

const KNOWN_STATUSES = ['draft', 'active', 'paused', 'ended'];
function statusLabel(status: string): string {
  return KNOWN_STATUSES.includes(status) ? t(`status.${status}`) : status;
}

export default function CampaignDetail() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const api = useApi();
  const { user } = useDotBotsAuth();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [rewards, setRewards] = useState<CampaignReward[]>([]);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canLaunch = hasPermission(user?.roles, 'campaigns.launch');

  async function load() {
    if (!campaignId) return;
    try {
      const [detail, statsRes] = await Promise.all([
        api<{ campaign: Campaign; rewards: CampaignReward[] }>(`/api/campaigns/${campaignId}`),
        api<CampaignStats>(`/api/campaigns/${campaignId}/stats`),
      ]);
      setCampaign(detail.campaign);
      setRewards(detail.rewards);
      setStats(statsRes);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  async function action(path: 'launch' | 'pause' | 'recalculate') {
    if (!campaignId) return;
    setBusy(true);
    try {
      await api(`/api/campaigns/${campaignId}/${path}`, { method: 'POST' });
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (error) return <div className="error">{error}</div>;
  if (!campaign || !stats) return <div className="muted">{t('common.loading')}</div>;

  const sourceData = Object.entries(stats.referralsBySource).map(([source, count]) => ({
    name: source,
    value: count,
  }));

  const endsAt = new Date(campaign.ends_at).getTime();
  const secondsRemaining = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
  const days = Math.floor(secondsRemaining / 86400);
  const hours = Math.floor((secondsRemaining % 86400) / 3600);

  return (
    <div>
      <div style={{ marginBottom: '0.5rem' }}>
        <Link className="small" to="/">{t('detail.allCampaigns')}</Link>
      </div>

      <div className="flex-between" style={{ marginBottom: '0.75rem' }}>
        <div>
          <h1 style={{ margin: 0 }}>{campaign.name}</h1>
          <div className="muted small">
            {campaign.product_name} · {t('detail.triggerLabel')}: {campaign.trigger_event}
            <span className={`badge ${campaign.status}`} style={{ marginLeft: '0.5rem' }}>
              {statusLabel(campaign.status)}
            </span>
          </div>
        </div>
        {canLaunch && (
          <div className="row">
            {campaign.status === 'draft' && (
              <button disabled={busy} onClick={() => action('launch')}>{t('detail.launch')}</button>
            )}
            {campaign.status === 'paused' && (
              <button disabled={busy} onClick={() => action('launch')}>{t('detail.resume')}</button>
            )}
            {campaign.status === 'active' && (
              <button className="secondary" disabled={busy} onClick={() => action('pause')}>
                {t('detail.pause')}
              </button>
            )}
            <button className="secondary" disabled={busy} onClick={() => action('recalculate')}>
              {t('detail.recalculate')}
            </button>
          </div>
        )}
      </div>

      {campaign.status === 'active' && (
        <div className="notice" style={{ marginBottom: '0.75rem' }}>
          {tp('detail.endsIn', { d: days, h: hours })}
        </div>
      )}

      <div className="stat-grid">
        <div className="card">
          <div className="stat-label">{t('stat.signups')}</div>
          <div className="stat-value">{stats.totalParticipants}</div>
        </div>
        <div className="card">
          <div className="stat-label">{t('stat.activated')}</div>
          <div className="stat-value">{stats.activatedParticipants}</div>
        </div>
        <div className="card">
          <div className="stat-label">{t('stat.rewardsHandedOut')}</div>
          <div className="stat-value">{stats.rewards.reduce((s, r) => s + r.winnersToDate, 0)}</div>
        </div>
        <div className="card">
          <div className="stat-label">{t('stat.realCost')}</div>
          <div className="stat-value">€{stats.costSummary.totalCostToDate.toFixed(2)}</div>
          <div className="muted small">
            {tp('stat.projected', { n: stats.costSummary.projectedTotalCost.toFixed(2) })}
          </div>
        </div>
      </div>

      <BudgetStatus budget={stats.budget} />


      <div className="grid-2" style={{ marginBottom: '1rem' }}>
        <div className="card">
          <h3 style={{ margin: '0 0 0.5rem' }}>{t('detail.signupsPerDay')}</h3>
          {stats.registrationsPerDay.length === 0 ? (
            <div className="muted">{t('detail.noSignupsYet')}</div>
          ) : (
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={stats.registrationsPerDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2a44" />
                  <XAxis dataKey="date" stroke="#8c98b6" />
                  <YAxis stroke="#8c98b6" allowDecimals={false} />
                  <Tooltip contentStyle={{ background: '#111a2e', border: '1px solid #1f2a44' }} />
                  <Bar dataKey="count" fill="#38bdf8" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <div className="card">
          <h3 style={{ margin: '0 0 0.5rem' }}>{t('detail.signupSource')}</h3>
          {sourceData.length === 0 ? (
            <div className="muted">{t('detail.noSourceData')}</div>
          ) : (
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={sourceData} dataKey="value" nameKey="name" outerRadius={80}>
                    {sourceData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#111a2e', border: '1px solid #1f2a44' }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <h3 style={{ margin: '0 0 0.5rem' }}>{t('detail.rewardBreakdown')}</h3>
        <table className="responsive-table">
          <thead>
            <tr>
              <th>{t('cost.col.reward')}</th>
              <th>{t('cost.col.trigger')}</th>
              <th style={{ textAlign: 'right' }}>{t('detail.col.winners')}</th>
              <th style={{ textAlign: 'right' }}>{t('detail.col.costPerWinner')}</th>
              <th style={{ textAlign: 'right' }}>{t('detail.col.total')}</th>
              <th style={{ textAlign: 'right' }}>{t('detail.col.projected')}</th>
            </tr>
          </thead>
          <tbody>
            {stats.rewards.map((r) => (
              <tr key={r.rewardId}>
                <td data-label={t('cost.col.reward')}>{r.rewardLabel}</td>
                <td data-label={t('cost.col.trigger')} className="small">
                  {r.triggerType}
                  {r.triggerValue ? ` (${r.triggerValue})` : ''}
                </td>
                <td data-label={t('detail.col.winners')} style={{ textAlign: 'right' }}>
                  {r.winnersToDate}
                </td>
                <td data-label={t('detail.col.costPerWinner')} style={{ textAlign: 'right' }}>
                  €{r.realCostPerWinner.toFixed(2)}
                </td>
                <td data-label={t('detail.col.total')} style={{ textAlign: 'right' }}>
                  €{r.totalCostToDate.toFixed(2)}
                </td>
                <td data-label={t('detail.col.projected')} style={{ textAlign: 'right' }}>
                  €{r.projectedTotalCost.toFixed(2)}
                </td>
              </tr>
            ))}
            {rewards.length === 0 && (
              <tr><td colSpan={6} className="muted">{t('detail.noRewards')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Leaderboard campaignId={campaign.id} />
    </div>
  );
}

function BudgetStatus({ budget }: { budget: CampaignStats['budget'] }) {
  const hasAnyConfig =
    budget.totalBudgetEur != null ||
    budget.dailyCapEur != null ||
    budget.rewardTrackStatus.some((r) => r.maxWinners != null);
  if (!hasAnyConfig) return null;

  const used = budget.usedPercent ?? 0;
  const totalBarColor =
    budget.hardCapReached ? '#ef4444' :
    budget.softCapReached ? '#f59e0b' :
    '#10b981';

  const dailyPct = budget.dailyCapEur != null && budget.dailyCapEur > 0
    ? Math.min(100, (budget.todayCostEur / budget.dailyCapEur) * 100)
    : null;
  const dailyBarColor =
    dailyPct != null && dailyPct >= 100 ? '#ef4444' :
    dailyPct != null && dailyPct >= 80 ? '#f59e0b' :
    '#10b981';

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div className="flex-between" style={{ marginBottom: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>{t('budget.statusTitle')}</h3>
        {budget.onHardCap && (
          <span className="badge small">
            {tp('budget.atHardCap', { action: budget.onHardCap })}
          </span>
        )}
      </div>

      {budget.totalBudgetEur != null && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div className="flex-between small">
            <span>
              {tp('budget.usedOf', {
                used: budget.totalCostToDate.toFixed(2),
                total: budget.totalBudgetEur.toFixed(2),
                pct: budget.usedPercent ?? 0,
              })}
            </span>
            <span className="muted">
              {tp('budget.softCapAt', {
                amount: (budget.softCapEur ?? 0).toFixed(0),
                pct: budget.softCapPercent,
              })}
            </span>
          </div>
          <div className="progress" style={{ marginTop: '0.25rem' }}>
            <div style={{ width: `${Math.min(used, 100)}%`, background: totalBarColor }} />
          </div>
          {budget.softCapReached && !budget.hardCapReached && (
            <div className="notice small" style={{ marginTop: '0.5rem' }}>
              {t('budget.softCapReached')}
            </div>
          )}
          {budget.hardCapReached && (
            <div className="error small" style={{ marginTop: '0.5rem' }}>
              {tp('budget.hardCapReached', { action: budget.onHardCap ?? '—' })}
            </div>
          )}
        </div>
      )}

      {budget.dailyCapEur != null && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div className="flex-between small">
            <span>
              {tp('budget.todayUsage', {
                today: budget.todayCostEur.toFixed(2),
                cap: budget.dailyCapEur.toFixed(2),
              })}
            </span>
            {budget.dailyCapReached && <span className="badge">{t('budget.capReached')}</span>}
          </div>
          <div className="progress" style={{ marginTop: '0.25rem' }}>
            <div style={{ width: `${dailyPct ?? 0}%`, background: dailyBarColor }} />
          </div>
        </div>
      )}

      {budget.rewardTrackStatus.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>{t('budget.col.reward')}</th>
              <th style={{ textAlign: 'right' }}>{t('budget.col.winners')}</th>
              <th style={{ textAlign: 'right' }}>{t('budget.col.max')}</th>
              <th>{t('budget.col.status')}</th>
            </tr>
          </thead>
          <tbody>
            {budget.rewardTrackStatus.map((r) => {
              const ratio = r.maxWinners != null && r.maxWinners > 0
                ? r.winnersToDate / r.maxWinners
                : 0;
              const statusKey = r.maxWinners == null
                ? 'budget.unlimited'
                : r.exhausted
                ? 'budget.exhausted'
                : ratio >= 0.8
                ? 'budget.almostFull'
                : 'budget.activeReward';
              return (
                <tr key={r.rewardId}>
                  <td>{r.rewardLabel}</td>
                  <td style={{ textAlign: 'right' }}>{r.winnersToDate}</td>
                  <td style={{ textAlign: 'right' }}>{r.maxWinners ?? '∞'}</td>
                  <td className="small">{t(statusKey)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
